import { alpha } from '@mui/material/styles';
import type { LandingTokens, MediaStyles, Sx } from '../types';
import { BREAKPOINTS, down } from './breakpoints';

const glowRing: Sx = {
  position: 'absolute',
  borderRadius: '50%',
  border: '1px solid rgba(255,255,255,.12)',
};

const CARD_BASE = 'linear-gradient(150deg, #1b2833, #0e161d 70%)';

const fill: Sx = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

export const mediaStyles = (t: LandingTokens): MediaStyles => ({
  image: fill,
  card: {
    background: [
      `radial-gradient(420px 300px at 70% 20%, ${alpha(t.brand, 0.38)}, transparent 65%)`,
      CARD_BASE,
    ].join(', '),
    border: '1px solid rgba(255,255,255,.1)',
    boxShadow: t.shadowLg,
    color: t.onDark,
  },
  // A button or a link when it plays, a plain box while there is no URL yet.
  poster: {
    ...fill,
    display: 'block',
    margin: 0,
    padding: 0,
    border: 0,
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    '&:is(a, button)': { cursor: 'pointer' },
    // The frame clips overflow, so the ring sits inside its edge.
    '&:focus-visible': { outline: `2px solid ${t.onDark}`, outlineOffset: '-4px' },
  },
  scene: {
    position: 'absolute',
    inset: 0,
    '&::before': { ...glowRing, right: -70, top: -70, width: 260, height: 260, content: '""' },
    '&::after': {
      ...glowRing,
      right: -30,
      top: -30,
      width: 180,
      height: 180,
      opacity: 0.6,
      content: '""',
    },
  },
  play: {
    position: 'absolute',
    left: '50%',
    top: '44%',
    transform: 'translate(-50%,-50%)',
    width: 76,
    height: 76,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: 'rgba(255,255,255,.94)',
    color: t.ink,
    boxShadow: '0 20px 50px -12px rgba(0,0,0,.6)',
    transition: 'transform .25s cubic-bezier(.2,.7,.2,1)',
    '& svg': { width: 26, height: 26, marginLeft: '3px' },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -14,
      borderRadius: '50%',
      border: '1px solid rgba(255,255,255,.35)',
      animation: 'landing-page-pulse 2.6s ease-out infinite',
    },
    '@keyframes landing-page-pulse': {
      '0%': { transform: 'scale(.8)', opacity: 0 },
      '35%': { opacity: 1 },
      '100%': { transform: 'scale(1.25)', opacity: 0 },
    },
    '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none' } },
    ':is(a, button):hover > &': { transform: 'translate(-50%,-50%) scale(1.08)' },
    [down(BREAKPOINTS.mobile)]: { width: 58, height: 58 },
  },
  meta: {
    position: 'absolute',
    left: 22,
    bottom: 20,
    right: 22,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '16px',
  },
  title: {
    fontFamily: t.serif,
    fontSize: 21,
    fontWeight: 420,
    color: t.onDark,
    [down(BREAKPOINTS.mobile)]: { fontSize: 17 },
  },
  caption: { fontSize: 13, color: alpha(t.onDark, 0.6), marginTop: '3px' },
  // Video is 16/9 and the frames are not, so the player letterboxes rather than reflowing the block.
  player: {
    ...fill,
    display: 'block',
    border: 0,
    backgroundColor: '#000',
    objectFit: 'contain',
  },
});
